import { BACKEND_API_BASE } from "../config/api";

const defaultOptions = { credentials: "include" };

/** Parsed refresh result shared by concurrent callers (StrictMode, 401 retries). */
let pendingRefresh = null;

async function parseError(response) {
  try {
    const data = await response.json();
    if (Array.isArray(data.detail)) {
      return data.detail.map((d) => d.msg).join(", ");
    }
    if (typeof data.detail === "string") {
      return data.detail;
    }
    return data.message || "Request failed";
  } catch {
    return response.statusText || "Request failed";
  }
}

function buildUrl(path) {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  const base = BACKEND_API_BASE.replace(/\/$/, "");
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return base ? `${base}${normalized}` : normalized;
}

/** Shared refresh for 401 retry (deduped; safe for concurrent awaiters). */
export async function refreshSession() {
  if (!pendingRefresh) {
    pendingRefresh = (async () => {
      const response = await fetch(buildUrl("/auth/refresh"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        const message = await parseError(response);
        throw new Error(message);
      }

      if (response.status === 204) {
        return null;
      }

      return response.json();
    })().finally(() => {
      pendingRefresh = null;
    });
  }

  return pendingRefresh;
}

/**
 * @param {string} path - API path (e.g. `/users`) or full URL
 * @param {RequestInit} options
 * @param {{ skipAuthRetry?: boolean }} config
 */
export async function apiRequest(path, options = {}, config = {}) {
  const { skipAuthRetry = false } = config;
  const url = buildUrl(path);

  const response = await fetch(url, {
    ...defaultOptions,
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (
    response.status === 401 &&
    !skipAuthRetry &&
    !path.includes("/auth/")
  ) {
    try {
      await refreshSession();
      return apiRequest(path, options, { skipAuthRetry: true });
    } catch (refreshError) {
      throw refreshError instanceof Error
        ? refreshError
        : new Error("Session expired");
    }
  }

  if (!response.ok) {
    const message = await parseError(response);
    throw new Error(message);
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
}
